<template>
  <router-view />
  <Transition>
    <CookieAcceptPopup v-if="showCookiePopup" :close="acceptCookies" />
  </Transition>
  <Transition>
    <SuccessPopup v-if="isSuccessPopupOpen" />
  </Transition>
</template>

<script setup>
import introPlanImage from '@/assets/img/introduction/plan.webp';
import { news } from '@/data/news';
import { getNewsThumbUrl } from '@/utils/newsImages';
import { getUserCaseBySlug } from '@/data/usersCases';
import { useRoute } from 'vue-router';
import { onMounted, ref, watch } from 'vue';
import { useStore } from '@nanostores/vue';
import CookieAcceptPopup from '@/Components/En/Common/CookieAcceptPopup.vue';
import SuccessPopup from '@/Components/EnNew/Modals/SuccessPopup/index.vue';
import config from './config';
import { store } from './store';
import { api } from './api';
import { localeStore, lx } from './locale';
import { sanitizeString } from './utils/sanitizeString';
import { registerAnonymousVisitor } from './utils/anonymousVisitor';

import 'swiper/swiper-bundle.css';

// ponytail: EN-страницы (index, News, Novelty, ForPersonalUsers) держат
// свой попап локально — гардим по lang, чтобы не было двойного баннера.
const isCookieAccept = ref(!!localStorage.getItem('show'));
const showCookiePopup = ref(localeStore.lang === 'ru' && !isCookieAccept.value);
const acceptCookies = () => { showCookiePopup.value = false; };

const isStage = config.appEnv !== 'production';
const isEmbedded = window.self !== window.top;
store.apiStore.setApi(api);
const isSuccessPopupOpen = useStore(store.successPopupStore.$isOpened);

defineOptions({ name: 'App' });

const { lang } = localeStore;
const route = useRoute();

const isWidgetPath = (path) => path.startsWith('/public_fp/') || path.startsWith('/widget');

const removeMetaTags = () => {
  const query = [
    'meta[name="description"]',
    'meta[name^="twitter:"]',
    'meta[property^="og:"]',
  ].join(',');
  document.querySelectorAll(query).forEach((el) => el.remove());
};

const createMetaName = (name, content) => {
  const meta = document.createElement('meta');
  meta.setAttribute('name', name);
  meta.content = sanitizeString(content);
  document.head.appendChild(meta);
};
const createMetaProperty = (property, content) => {
  const meta = document.createElement('meta');
  meta.setAttribute('property', property);
  meta.content = sanitizeString(content);
  document.head.appendChild(meta);
};
const applyBaseMetaTags = (metaContent) => {
  const { metaTitleContent, metaDescriptionContent } = metaContent;
  document.title = metaTitleContent;
  createMetaName('description', metaDescriptionContent);
};
const applyOpenGraphMetaTags = (metaContent) => {
  const { metaTitleContent, metaDescriptionContent, metaImage } = metaContent;
  const domain = lx('hostname');
  const ogImageUrl = metaImage && metaImage.startsWith('http')
    ? metaImage
    : `${domain}${metaImage || ''}`;
  const [pathWithoutQuery] = route.path.split('?');
  const path = pathWithoutQuery || route.path;
  const canonicalUrl = isWidgetPath(path) ? `${domain}/` : domain + path;
  const ogType = route.name === 'novelty' ? 'article' : 'website';

  createMetaProperty('og:title', metaTitleContent || document.title);
  createMetaProperty('og:description', metaDescriptionContent);
  if (ogImageUrl) {
    createMetaProperty('og:image', ogImageUrl);
  }
  createMetaProperty('og:url', canonicalUrl);
  createMetaProperty('og:type', ogType);
  const siteName = lang === 'ru' ? 'HartEstate' : 'Getfloorplan';
  createMetaProperty('og:site_name', siteName);
};

const updateTwitterMetaTags = (metaContent) => {
  const { metaTitleContent, metaDescriptionContent, metaImage } = metaContent;
  const twitterDomain = lx('hostname');
  const twitterImageUrl = metaImage && metaImage.startsWith('http')
      ? metaImage
      : `${twitterDomain}${metaImage || ''}`;
  createMetaName('twitter:card', 'summary_large_image');
  createMetaName('twitter:title', metaTitleContent || document.title);
  createMetaName('twitter:description', metaDescriptionContent);
  if (twitterImageUrl) {
    createMetaName('twitter:image', twitterImageUrl);
  }
};

const getMetaContent = () => {
  if (route.name === 'novelty' && route.params?.slug) {
    const newsItem = news.find((item) => item.id === route.params.slug);
    if (newsItem) {
      return {
        // Title may contain inline markup (e.g. a coloured <span>) meant only
        // for the rendered <h1>; strip tags so document.title/og stay clean.
        metaTitleContent: (newsItem.metaTitle || newsItem.title).replace(/<[^>]*>/g, ''),
        metaDescriptionContent: newsItem.metaDescription || newsItem.description,
        metaImage: newsItem.thumb ? getNewsThumbUrl(newsItem.thumb) : '/logo.png',
      };
    }
  }

  if (route.name === 'case-page' && route.params?.slug) {
    const caseItem = getUserCaseBySlug(route.params.slug);
    if (caseItem) {
      return {
        metaTitleContent: caseItem.metaTitle || caseItem.title,
        metaDescriptionContent: caseItem.metaDescription || caseItem.subtitle,
        metaImage: caseItem.images?.hero || '/logo.png',
      };
    }
  }

  return {
    metaTitleContent: route.meta?.title || lx('title'),
    metaDescriptionContent: lx('description'),
    metaImage: route.meta?.image || introPlanImage,
  };
};

const updateMetaTags = () => {
  removeMetaTags();
  const metaContent = getMetaContent(route);
  applyBaseMetaTags(metaContent);
  applyOpenGraphMetaTags(metaContent, route);
  updateTwitterMetaTags(metaContent);
};

const updateCanonicalLink = () => {
  const oldLink = document.querySelector('link[rel="canonical"]');
  if (oldLink) oldLink.remove();

  const [pathWithoutQuery] = route.path.split('?');
  const path = pathWithoutQuery || route.path;

  const domain = lx('hostname');
  const canonicalUrl = isWidgetPath(path) ? `${domain}/` : domain + path;

  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', canonicalUrl);

  document.head.appendChild(link);
};

const updateRobotsTag = () => {
  const isNoRobots = isStage || isWidgetPath(route.path);

  let metaRobots = document.querySelector('meta[name="robots"]');
  if (!isNoRobots) {
    if (metaRobots) metaRobots.remove();
    return;
  }

  const content = 'noindex, nofollow';
  if (!metaRobots) metaRobots = createMetaName('robots', content);
  else metaRobots.content = content;
};

const saveUtmParamsToLocalStorage = () => {
  const { search } = window.location;
  const urlParams = new URLSearchParams(search);
  const utmParams = {};

  Array.from(urlParams).forEach(([key, value]) => {
    if (key.startsWith('utm_')) {
      utmParams[key] = value;
    }
  });

  if (Object.keys(utmParams).length > 0) {
    localStorage.setItem('utm_params', JSON.stringify(utmParams));
  } else {
    localStorage.removeItem('utm_params');
  }
};

const updateMeta = () => {
  updateMetaTags();
  updateCanonicalLink();
  updateRobotsTag();
  if (!isEmbedded) saveUtmParamsToLocalStorage();
};

watch(() => route.fullPath, updateMeta);
onMounted(() => {
  store.init();
  if (!isEmbedded) registerAnonymousVisitor().catch(() => {});
  store.userStore.authorize().then(updateMeta);
});
</script>

<style lang="sass" scoped>

body
  background-color: white

</style>
